import { useEffect, useRef, useState, useCallback } from 'react';

const SIMULATED_ONLINE_MACS = new Set([]);

function getWsUrl() {
  // Always use the current browser's host — works on EC2, ngrok, localhost, anywhere
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname;
  const port = window.location.port;

  if (process.env.NODE_ENV === 'production') {
    // In production (served from port 4001), WS is on the same host/port
    return `${protocol}//${host}${port ? ':' + port : ''}/ws`;
  }
  // In dev (React on 3000), backend WS is on 4001
  return `ws://${host}:4001`;
}

function macFromTopic(topic) {
  const parts = (topic || '').split('/');
  return parts.length >= 3 ? parts[2].toUpperCase() : null;
}

function toAckTopic(pingTopic) {
  return pingTopic + 'Ack';
}

export function useMqttProxy() {
  const wsRef        = useRef(null);
  const listenersRef = useRef({});
  const [connected, setConnected] = useState(false);

  const fireLocal = useCallback((topic, message) => {
    const cbs = listenersRef.current[topic];
    if (cbs && cbs.size > 0) {
      console.log('🤖 [SIM-ACK] Firing local ACK on:', topic);
      cbs.forEach(cb => cb(message, topic));
    }
  }, []);

  useEffect(() => {
    const WS_URL = getWsUrl();
    const wsUrl = WS_URL.includes('ngrok')
      ? WS_URL + '?ngrok-skip-browser-warning=true'
      : WS_URL;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => console.log('WS proxy: socket open');

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'connected') {
          setConnected(true);
        } else if (msg.type === 'message') {
          console.log('[WS RX] topic=' + msg.topic + ' msg=' + (typeof msg.message === 'string' ? msg.message.slice(0,120) : JSON.stringify(msg.message)));
          const cbs = listenersRef.current[msg.topic];
          if (cbs && cbs.size > 0) {
            cbs.forEach(cb => cb(msg.message, msg.topic));
          } else {
            Object.entries(listenersRef.current).forEach(([subTopic, subCbs]) => {
              if (subCbs.size > 0 && (
                msg.topic === subTopic ||
                msg.topic.endsWith(subTopic) ||
                subTopic.endsWith(msg.topic)
              )) {
                console.log('[WS RX] Fuzzy match: broker=' + msg.topic + ' sub=' + subTopic);
                subCbs.forEach(cb => cb(msg.message, subTopic));
              }
            });
          }
        } else if (msg.type === 'error') {
          console.error('MQTT proxy error:', msg.error);
        }
      } catch (e) {
        console.error('WS parse error', e);
      }
    };

    ws.onclose = () => { setConnected(false); console.log('WS proxy: closed'); };
    ws.onerror = (e) => console.error('WS proxy: error', e);

    return () => ws.close();
  }, []);

  const subscribe = useCallback((topic, callback) => {
    if (!listenersRef.current[topic]) {
      listenersRef.current[topic] = new Set();
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'subscribe', topic }));
      }
    }
    listenersRef.current[topic].add(callback);
    return () => {
      if (listenersRef.current[topic]) {
        listenersRef.current[topic].delete(callback);
      }
    };
  }, []);

  const publish = useCallback((topic, message) => {
    if (message === undefined) message = 'ping';
    const mac = macFromTopic(topic);
    if (mac && SIMULATED_ONLINE_MACS.has(mac) && message === 'ping') {
      const ackTopic = toAckTopic(topic);
      const delay = 50 + Math.random() * 100;
      console.log('🤖 [SIM] ping for ' + mac + ' → auto-ACK in ' + Math.round(delay) + 'ms on ' + ackTopic);
      setTimeout(() => fireLocal(ackTopic, 'ack'), delay);
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'publish', topic, message }));
    } else {
      console.warn('WS not open — cannot publish');
    }
  }, [fireLocal]);

  return { connected, subscribe, publish };
}
